/**
 * Strix Healthcare — tRPC Client + Provider
 *
 * Establishes a type-safe tRPC client connected to the Express/tRPC backend.
 * Execution tokens (x-execution-token) for governed mutations are automatically
 * injected from sessionStorage when present.
 *
 * Exports TrpcProvider for wrapping the app in main.tsx.
 */

import React, { useState } from 'react';
import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink, loggerLink } from '@trpc/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import superjson from 'superjson';

// Import the AppRouter type from the server — source of type truth
import type { AppRouter } from '../../../server/routers/index';

// Re-export for use across the app
export type { AppRouter };

/** Typed tRPC React client — use this everywhere in the app */
export const trpc = createTRPCReact<AppRouter>();

/** Minimal view of the Clerk instance attached to window by @clerk/clerk-js. */
interface ClerkWindowInstance {
  session?: {
    getToken: () => Promise<string | null>;
  } | null;
}

declare global {
  interface Window {
    /** Where @clerk/clerk-js publishes its instance. */
    Clerk?: ClerkWindowInstance;
    /** Never set by Clerk or by this app; kept only so an older embedding that sets it still works. */
    __clerk?: ClerkWindowInstance;
  }
}

/**
 * The Clerk instance, if it has loaded. clerk-js publishes it as `window.Clerk`.
 * This used to read only `window.__clerk`, which nothing ever sets, so in Clerk
 * mode no request carried the session token and the server saw every call as
 * anonymous ("Authentication required").
 */
export function getClerkInstance(win: Pick<Window, 'Clerk' | '__clerk'> = window): ClerkWindowInstance | undefined {
  return win.Clerk ?? win.__clerk;
}

/**
 * Get the current Clerk auth token.
 */
export async function getAuthToken(): Promise<string | null> {
  try {
    const clerk = getClerkInstance();
    if (clerk?.session) {
      const token = await clerk.session.getToken();
      if (token) return token;
    }
  } catch (err: unknown) {
    // Clerk not initialised yet — fall through to demo token
    console.debug('[trpc] Clerk token unavailable, trying demo token', err instanceof Error ? err.message : String(err));
  }
  // Demo mode (no Clerk): use the JWT minted by /api/auth/demo-login.
  try {
    const demoToken = localStorage.getItem('strix_demo_token');
    if (demoToken) return demoToken;
  } catch (err: unknown) {
    // localStorage unavailable (private browsing mode)
    console.debug('[trpc] localStorage unavailable', err instanceof Error ? err.message : String(err));
  }
  return null;
}

/**
 * Creates the tRPC client with auth headers and governance token injection.
 */
function createTrpcClient() {
  return trpc.createClient({
    links: [
      // Logger only in development
      ...(import.meta.env.DEV
        ? [loggerLink({ enabled: () => true })]
        : []),

      httpBatchLink({
        url: '/api/trpc',
        transformer: superjson,

        headers: async () => {
          const token = await getAuthToken();

          // Execution tokens for Strix CPF governance
          const executionToken = sessionStorage.getItem('x-execution-token');

          return {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(executionToken ? { 'x-execution-token': executionToken } : {}),
          };
        },
      }),
    ],
  });
}

/**
 * TrpcProvider — wraps app with tRPC + React Query providers.
 * Used by main.tsx.
 */
export function TrpcProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
      },
    },
  }));
  const [trpcClient] = useState(() => createTrpcClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </trpc.Provider>
  );
}

/**
 * Store an execution token for the next governed mutation call.
 */
export function storeExecutionToken(token: string): void {
  sessionStorage.setItem('x-execution-token', token);
}

/**
 * Clear the execution token after use.
 */
export function clearExecutionToken(): void {
  sessionStorage.removeItem('x-execution-token');
}

